import SuspenseLoader from 'component/base/Loading/SuspenseLoader';
import BaseLayout from 'component/layout/BaseLayout';
import GlobalStylesLayout from 'component/layout/GlobalStylesLayout';
import Route from 'component/root/LazyRoute';
import { Provider as UserOnboardingFormProvider } from 'providers/UserOnboardingForm';
import React, { lazy, memo } from 'react';
import { Switch } from 'react-router-dom';

import paths from './paths';

const AuthComplete = lazy(
  () => import('component/base/Office365/AuthComplete'),
);

const ConsentComplete = lazy(
  () => import('component/base/Office365/ConsentComplete'),
);

const OnboardingInviteView = lazy(
  () => import('component/view/Onboarding/OnboardingInviteView'),
);

const OnboardingConfirmUser = lazy(
  () => import('component/view/Onboarding/OnboardingConfirmUser'),
);

const OnboardingManualEmailView = lazy(
  () => import('component/view/Onboarding/OnboardingManualEmailView'),
);

const OnboardingManualTokenView = lazy(
  () => import('component/view/Onboarding/OnboardingManualTokenView'),
);

const OnboardingDownloadAppView = lazy(
  () => import('component/view/Onboarding/OnboardingDownloadAppView'),
);

const OnboardingExpiredView = lazy(
  () => import('component/view/Onboarding/OnboardingExpiredView'),
);

const OnboardingExistsView = lazy(
  () => import('component/view/Onboarding/OnboardingExistsView'),
);

const OnboardingReadyView = lazy(
  () => import('component/view/Onboarding/OnboardingReadyView'),
);

const OnboardingIntegrationView = lazy(
  () => import('component/view/Onboarding/OnboardingIntegrationView'),
);

const OnboardingIntegrationOtherView = lazy(
  () => import('component/view/Onboarding/OnboardingIntegrationOtherView'),
);

const OnboardingIntegration365AuthView = lazy(
  () => import('component/view/Onboarding/OnboardingIntegration365AuthView'),
);

const Integration365View = lazy(
  () => import('component/view/Signup/Integration365View'),
);

const LazyAuthComplete = () => (
  <SuspenseLoader
    publicPath={paths.OFFICE365_AUTH_COMPLETE}
    filePath="component/base/Office365/AuthComplete"
  >
    <AuthComplete />
  </SuspenseLoader>
);

const LazyConsentComplete = () => (
  <SuspenseLoader
    publicPath={paths.OFFICE365_CONSENT_COMPLETE}
    filePath="component/base/Office365/ConsentComplete"
  >
    <ConsentComplete />
  </SuspenseLoader>
);

const LazyOnboardingInviteView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_INVITE}
    filePath="component/view/Onboarding/OnboardingInviteView"
  >
    <OnboardingInviteView />
  </SuspenseLoader>
);

const LazyOnboardingConfirmUser = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_CONFIRM}
    filePath="component/view/Onboarding/OnboardingConfirmUser"
  >
    <OnboardingConfirmUser />
  </SuspenseLoader>
);

const LazyOnboardingManualEmailView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_REGISTER}
    filePath="component/view/Onboarding/OnboardingManualEmailView"
  >
    <OnboardingManualEmailView />
  </SuspenseLoader>
);

const LazyOnboardingManualTokenView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_REGISTER_ACTIVATE}
    filePath="component/view/Onboarding/OnboardingManualTokenView"
  >
    <OnboardingManualTokenView />
  </SuspenseLoader>
);

const LazyOnboardingDownloadAppView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_DOWNLOAD}
    filePath="component/view/Onboarding/OnboardingDownloadAppView"
  >
    <OnboardingDownloadAppView />
  </SuspenseLoader>
);

const LazyOnboardingExpiredView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_EXPIRED}
    filePath="component/view/Onboarding/OnboardingExpiredView"
  >
    <OnboardingExpiredView />
  </SuspenseLoader>
);

const LazyOnboardingExistsView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_EXISTS}
    filePath="component/view/Onboarding/OnboardingExistsView"
  >
    <OnboardingExistsView />
  </SuspenseLoader>
);

const LazyOnboardingReadyView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_READY}
    filePath="component/view/Onboarding/OnboardingReadyView"
  >
    <OnboardingReadyView />
  </SuspenseLoader>
);

const LazyOnboardingIntegrationView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_INTEGRATION}
    filePath="component/view/Onboarding/OnboardingIntegrationView"
  >
    <OnboardingIntegrationView />
  </SuspenseLoader>
);

const LazyOnboardingIntegrationOtherView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_INTEGRATION_OTHER}
    filePath="component/view/Onboarding/OnboardingIntegrationOtherView"
  >
    <OnboardingIntegrationOtherView />
  </SuspenseLoader>
);

const LazyOnboardingIntegration365AuthView = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_INTEGRATION_OFFICE365_AUTH}
    filePath="component/view/Onboarding/OnboardingIntegration365AuthView"
  >
    <OnboardingIntegration365AuthView />
  </SuspenseLoader>
);

const LazyIntegration365View = () => (
  <SuspenseLoader
    publicPath={paths.ONBOARDING_INTEGRATION_OFFICE365_AUTH_ID}
    filePath="component/view/Onboarding/Integration365View"
  >
    <Integration365View />
  </SuspenseLoader>
);

/**
 * Group of components that share the same narrow dashboard
 * layout without a nav panel
 */
const BaseLayoutPage = () => {
  return (
    <GlobalStylesLayout>
      <BaseLayout>
        <UserOnboardingFormProvider>
          <Switch>
            <Route exact path={paths.OFFICE365_AUTH_COMPLETE}>
              <LazyAuthComplete />
            </Route>
            <Route exact path={paths.OFFICE365_CONSENT_COMPLETE}>
              <LazyConsentComplete />
            </Route>
            <Route anonymous exact path={paths.ONBOARDING_INVITE}>
              <LazyOnboardingInviteView />
            </Route>
            <Route exact path={paths.ONBOARDING_CONFIRM}>
              <LazyOnboardingConfirmUser />
            </Route>
            <Route exact path={paths.ONBOARDING_REGISTER}>
              <LazyOnboardingManualEmailView />
            </Route>
            <Route exact path={paths.ONBOARDING_REGISTER_ACTIVATE}>
              <LazyOnboardingManualTokenView />
            </Route>
            <Route exact path={paths.ONBOARDING_DOWNLOAD}>
              <LazyOnboardingDownloadAppView />
            </Route>
            <Route exact path={paths.ONBOARDING_EXPIRED}>
              <LazyOnboardingExpiredView />
            </Route>
            <Route exact path={paths.ONBOARDING_EXISTS}>
              <LazyOnboardingExistsView />
            </Route>
            <Route authed exact path={paths.ONBOARDING_READY}>
              <LazyOnboardingReadyView />
            </Route>
            <Route authed exact path={paths.ONBOARDING_INTEGRATION}>
              <LazyOnboardingIntegrationView />
            </Route>
            <Route authed exact path={paths.ONBOARDING_INTEGRATION_OTHER}>
              <LazyOnboardingIntegrationOtherView />
            </Route>
            <Route
              authed
              exact
              path={paths.ONBOARDING_INTEGRATION_OFFICE365_AUTH}
            >
              <LazyOnboardingIntegration365AuthView />
            </Route>
            <Route
              authed
              exact
              path={paths.ONBOARDING_INTEGRATION_OFFICE365_AUTH_ID}
            >
              <LazyIntegration365View />
            </Route>
          </Switch>
        </UserOnboardingFormProvider>
      </BaseLayout>
    </GlobalStylesLayout>
  );
};

/*
  Memoisation here prevents re-rendering of layout components
  on change of nested routes.
*/
export default memo(BaseLayoutPage);

/*


*/
